import React, { useState } from 'react';
import { 
  FaBolt, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaShieldAlt, 
  FaChevronRight, 
  FaUndo 
} from 'react-icons/fa';

// Mock contextually generated questions based on an engineering commit log
const MOCK_QUIZ_QUESTIONS = [
  {
    id: "q1",
    question: "When initializing a dynamic schema change inside a Knex.js transaction block, how do you guarantee database lock safety to prevent cascading deadlock failures?",
    options: [
      "Wrap the schema change utilizing an absolute schema reference bypassing the trx wrapper.",
      "Pass the explicit transaction boundary instance handler block (trx) cleanly into the schema runner execution chain.",
      "Execute an internal process flush command before launching migrations concurrently.",
      "Knex automatically scopes all root schema changes inside an exclusive table lock configuration by default."
    ],
    correctIndex: 1
  },
  {
    id: "q2",
    question: "Which schema model definition strategy provides the most optimal execution speed performance when isolating highly dynamic missing-item lookups via Knex queries?",
    options: [
      "Enforcing a fixed column mapping layer array without unique key identifiers.",
      "Utilizing JSONB storage containers with partial GIN indexes mapped over targeted sub-keys.",
      "Relying completely on standard raw text column definitions with string pattern checking queries.",
      "Creating nested sub-tables for every individual metadata variant tracked."
    ],
    correctIndex: 1
  },
  {
    id: "q3",
    question: "What is the primary risk of using raw connection queries inside an automated data processing worker pipeline handling unstructured third-party payloads?",
    options: [
      "SQL injection vectors if untrusted variables are concatenated directly into execution statements instead of sanitized parameters.",
      "Automatic memory pool recycling forcing connection disconnections.",
      "Strict data truncation errors occurring on integer data parsing arrays.",
      "Loss of basic API credential tokens inside configuration environments."
    ],
    correctIndex: 0
  }
];

export default function VerificationLoop({ commitContext, onQuizComplete, onQuizFail }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0);

  const currentQuestionData = MOCK_QUIZ_QUESTIONS[currentStep];
  const isLastStep = currentStep === MOCK_QUIZ_QUESTIONS.length - 1;

  const handleOptionClick = (index) => {
    if (isAnswered) return;
    setSelectedOption(index);
    setIsAnswered(true);

    const isCorrect = index === currentQuestionData.correctIndex;
    if (isCorrect) {
      setCorrectAnswersCount(prev => prev + 1);
    }
  };

  const handleNext = () => {
    if (!isAnswered) return;

    if (!isLastStep) {
      setCurrentStep(prev => prev + 1);
      setSelectedOption(null);
      setIsAnswered(false);
    } else {
      // Evaluation gate after finishing the 3-question block
      const finalScore = correctAnswersCount + (selectedOption === currentQuestionData.correctIndex ? 1 : 0);
      const passed = finalScore === MOCK_QUIZ_QUESTIONS.length; // Requires perfect 3/3 score to pass verification

      if (passed && onQuizComplete) {
        onQuizComplete();
      } else if (!passed && onQuizFail) {
        onQuizFail({ score: finalScore, total: MOCK_QUIZ_QUESTIONS.length });
      }
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.quizBox}>
        
        {/* Verification Status Header */}
        <div style={styles.quizHeader}>
          <div style={styles.headerLeft}>
            <FaShieldAlt style={styles.shieldIcon} />
            <span style={styles.headerTitle}>Verification Engine</span>
          </div>
          <div style={styles.stepIndicator}>
            Question {currentStep + 1} of {MOCK_QUIZ_QUESTIONS.length}
          </div>
        </div>

        {/* Dynamic Context Notification Banner */}
        <div style={styles.contextBanner}>
          <FaBolt style={styles.boltIcon} />
          <span style={styles.contextText}>
            Validating intent for: <strong>{commitContext?.tool || 'Current Task Stack'}</strong>
          </span>
        </div>

        {/* Progress Tracker Horizontal Visualization Line */}
        <div style={styles.progressBarWrapper}>
          {MOCK_QUIZ_QUESTIONS.map((_, idx) => (
            <div 
              key={idx} 
              style={{
                ...styles.progressSegment,
                ...(idx <= currentStep ? styles.activeSegment : {}),
                ...(idx < currentStep ? styles.completedSegment : {})
              }}
            />
          ))}
        </div>

        {/* Quiz Core Content Card */}
        <div style={styles.questionBody}>
          <h3 style={styles.questionText}>{currentQuestionData.question}</h3>
          
          <div style={styles.optionsList}>
            {currentQuestionData.options.map((option, idx) => {
              // Computing custom color theme states for selected and verified rows
              let optionStyle = { ...styles.optionCard };
              let indicatorIcon = null;

              if (isAnswered) {
                if (idx === currentQuestionData.correctIndex) {
                  optionStyle = { ...optionStyle, ...styles.correctOption };
                  indicatorIcon = <FaCheckCircle style={styles.correctIcon} />;
                } else if (idx === selectedOption) {
                  optionStyle = { ...optionStyle, ...styles.wrongOption };
                  indicatorIcon = <FaTimesCircle style={styles.wrongIcon} />;
                } else {
                  optionStyle = { ...optionStyle, ...styles.fadedOption };
                }
              } else if (selectedOption === idx) {
                optionStyle = { ...optionStyle, ...styles.selectedOption };
              }

              return (
                <button
                  key={idx}
                  onClick={() => handleOptionClick(idx)}
                  disabled={isAnswered}
                  style={optionStyle}
                >
                  <span style={styles.optionLetter}>{String.fromCharCode(65 + idx)}.</span>
                  <span style={styles.optionTextMain}>{option}</span>
                  {indicatorIcon}
                </button>
              );
            })}
          </div>
        </div>

        {/* Next Control Action Footer Strip */}
        <div style={styles.quizFooter}>
          <div style={styles.footerHelpText}>
            All answers require explicit verification accuracy to guarantee public credential trust.
          </div>
          <button
            onClick={handleNext}
            disabled={!isAnswered}
            style={{ 
              ...styles.nextButton, 
              ...(!isAnswered ? styles.disabledNextButton : {}) 
            }}
          >
            {isLastStep ? "Finalize Evidence" : "Continue"} 
            <FaChevronRight style={{ marginLeft: '6px', fontSize: '11px' }} />
          </button>
        </div>

      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: '#0d1117',
    padding: '24px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  },
  quizBox: {
    width: '100%',
    maxWidth: '680px',
    backgroundColor: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 12px 32px rgba(0,0,0,0.4)'
  },
  quizHeader: {
    backgroundColor: '#21262d',
    borderBottom: '1px solid #30363d',
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  shieldIcon: {
    color: '#58a6ff',
    fontSize: '16px'
  },
  headerTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#f0f6fc'
  },
  stepIndicator: {
    fontSize: '12px',
    color: '#8b949e',
    fontWeight: '500'
  },
  contextBanner: {
    backgroundColor: 'rgba(56, 139, 253, 0.08)',
    borderBottom: '1px solid #30363d',
    padding: '10px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  boltIcon: {
    color: '#d29922',
    fontSize: '13px'
  },
  contextText: {
    fontSize: '13px',
    color: '#c9d1d9'
  },
  progressBarWrapper: {
    display: 'flex',
    height: '3px',
    width: '100%',
    backgroundColor: '#21262d'
  },
  progressSegment: {
    flexGrow: 1,
    backgroundColor: 'transparent',
    transition: 'background-color 0.3s ease'
  },
  activeSegment: {
    backgroundColor: '#388bfd'
  },
  completedSegment: {
    backgroundColor: '#238636'
  },
  questionBody: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  questionText: {
    fontSize: '16px',
    fontWeight: '500',
    lineHeight: '1.5',
    margin: 0,
    color: '#f0f6fc'
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  optionCard: {
    backgroundColor: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '14px 16px',
    textAlign: 'left',
    color: '#c9d1d9',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    width: '100%',
    outline: 'none',
    transition: 'all 0.15s ease'
  },
  selectedOption: {
    borderColor: '#58a6ff',
    backgroundColor: 'rgba(56, 139, 253, 0.05)'
  },
  correctOption: {
    borderColor: '#238636',
    backgroundColor: 'rgba(35, 134, 54, 0.1)',
    color: '#39d353'
  },
  wrongOption: {
    borderColor: '#da3637',
    backgroundColor: 'rgba(218, 54, 55, 0.1)',
    color: '#f85149'
  },
  fadedOption: {
    opacity: 0.4,
    cursor: 'not-allowed'
  },
  optionLetter: {
    fontWeight: '600',
    color: '#8b949e',
    flexShrink: 0
  },
  optionTextMain: {
    flexGrow: 1,
    lineHeight: '1.4'
  },
  correctIcon: {
    color: '#39d353',
    marginTop: '2px',
    flexShrink: 0
  },
  wrongIcon: {
    color: '#f85149',
    marginTop: '2px',
    flexShrink: 0
  },
  quizFooter: {
    borderTop: '1px solid #30363d',
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px'
  },
  footerHelpText: {
    fontSize: '12px',
    color: '#8b949e',
    lineHeight: '1.4',
    maxWidth: '400px'
  },
  nextButton: {
    backgroundColor: '#21262d',
    border: '1px solid #30363d',
    borderRadius: '6px',
    color: '#c9d1d9',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    transition: 'all 0.2s'
  },
  disabledNextButton: {
    opacity: 0.5,
    cursor: 'not-allowed'
  }
};